/*
 * "Browse channels" (`GET /api/chat/channels`): every channel the reader's groups can find, joined or
 * not, and the ones the reader is in. It is the other half of "everybody in a channel's groups may
 * join it" — without a list of them, the permission exists and nothing exercises it.
 *
 * A private conversation is never in that list, because the route answers `chat.conversation.browse`
 * and that is what browse means. The filter is a request rather than a client-side scan: the server
 * has the list, and a member of a big instance should not have to download all of it to find one
 * name.
 *
 * An administrator gets a second list under it, *Other channels*: the channels their groups do not
 * reach and the private channels they are not in, read from the administrator's listing
 * (`GET /api/admin/chat/conversations`), which names a channel and its groups and never who is in it.
 * *View* goes to the channel's address, where the administrator sees its head and members and none
 * of its messages (`AdminOutside`). The listing has no query, so the same field filters it here, by
 * short name and name. Nobody else ever asks for it.
 */

import { useEffect, useState } from "react"
import { t as coreT } from "../../../core/i18n/index.ts"
import { isAdmin } from "../../../core/people/standing.ts"
import { useModel } from "../../../core/store/store.ts"
import { useAsyncAction } from "../../../lib/useAsyncAction.ts"
import { Button, Dialog, emojiText, Field, FormAlert, ListState } from "../../../ui/index.ts"
import {
	browseChannels,
	joinConversation,
	listAdminConversations,
	type ChannelSummary,
} from "../conversation/api.ts"
import { goTo, whenConversationArrives } from "../conversation/opening.ts"
import { channelKindLabel } from "../conversation/owners.ts"
import { t } from "../i18n/t.ts"
import { conversationPath } from "../navigation.ts"
import type { AdminConversation } from "../wire/views.ts"
import "./sidebar.css"

/** How a row names its channel where a sentence needs it: "#test-day", never an empty string. */
function channelName(row: ChannelSummary): string {
	return `#${row.conversation.slug ?? ""}`
}

export function BrowseChannelsDialog({
	open,
	onOpenChange,
	onCreate,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/**
	 * The door out of an instance with no channel the reader can find: an empty list without an
	 * action is a dead end (VISUAL.md §7.17), and the one thing that changes it is a channel of one's
	 * own. The caller owns the dialog that does it, because it owns this one too (`ChatSidebar.tsx`).
	 * Absent for a reader who may not create one — a guest — whose empty list then offers nothing.
	 */
	onCreate?: () => void
}) {
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	const [query, setQuery] = useState("")
	const [channels, setChannels] = useState<ChannelSummary[] | null>(null)

	const load = useAsyncAction(async (q: string) => {
		try {
			setChannels((await browseChannels(q)).channels)
		} catch (err) {
			// An empty list under the refusal, so the dialog says "nothing here" rather than
			// waiting for a page that is never coming.
			setChannels([])
			throw err
		}
	})

	const loadChannels = load.run

	const join = useAsyncAction(async (row: ChannelSummary) => {
		await joinConversation(row.conversation.id)
		await whenConversationArrives(row.conversation.id)
		onOpenChange(false)
		goTo(conversationPath(row.conversation.slug ?? ""))
	})

	// The list exists only on the server and nothing here can derive it, so opening the dialog is
	// what fetches it; the state changes happen after the await rather than during a render.
	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (!open) return
		setChannels(null)
		setQuery("")
		void loadChannels("")
	}, [open, loadChannels])
	/* eslint-enable react-hooks/set-state-in-effect */

	function openChannel(row: ChannelSummary) {
		onOpenChange(false)
		goTo(conversationPath(row.conversation.slug ?? ""))
	}

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("chat.browse.title")}
			description={t("chat.browse.body")}
			footer={
				<div className="tb-dialog__actions">
					<Button
						variant="neutral"
						label={coreT("action.close")}
						onClick={() => onOpenChange(false)}
					/>
				</div>
			}
		>
			<form
				className="tb-fields"
				onSubmit={(event) => {
					event.preventDefault()
					void loadChannels(query.trim())
				}}
			>
				<Field label={t("chat.browse.filter")}>
					{(props) => (
						<input
							{...props}
							className="tb-field__control"
							type="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
					)}
				</Field>
			</form>
			<FormAlert messageKey={join.error ?? load.error} />
			{channels === null ? (
				<ListState.Loading rows={4} label={t("chat.browse.loading")} />
			) : channels.length === 0 ? (
				// A filter with no answer offers the filter back; no channel to find at all offers the one
				// thing that changes that, to a reader who may make one (VISUAL.md §7.17).
				<ListState.Empty
					title={t("chat.browse.empty")}
					action={
						query === "" ? (
							onCreate === undefined ? undefined : (
								<Button
									variant="primary"
									label={t("chat.sidebar.newChannel")}
									onClick={() => onCreate()}
								/>
							)
						) : (
							<Button
								variant="secondary"
								label={t("chat.browse.clear")}
								onClick={() => {
									setQuery("")
									void loadChannels("")
								}}
							/>
						)
					}
				/>
			) : (
				<ul className="tb-browse__list">
					{channels.map((row) => (
						<li key={row.conversation.id} className="tb-browse__row">
							{/* The channel as the place it is — `#slug`, hash and all, which is what the address
							    bar and everybody in the team say (X-A3 V21) — and under it what it is for. */}
							<span className="tb-browse__about">
								<span className="tb-browse__name">{channelName(row)}</span>
								<span className="tb-browse__meta">
									{emojiText(
										row.conversation.topic === undefined || row.conversation.topic === ""
											? t("chat.conversation.members", { count: row.conversation.memberCount })
											: t("chat.browse.line", {
													members: t("chat.conversation.members", {
														count: row.conversation.memberCount,
													}),
													topic: row.conversation.topic,
												}),
									)}
								</span>
							</span>
							{/* A channel the reader is already in says so rather than offering to join it twice,
							    and the control beside that fact opens it (D-10 P7). */}
							{row.joined ? <span className="tb-pill">{t("chat.browse.joined")}</span> : null}
							<Button
								variant="neutral"
								size="sm"
								label={row.joined ? t("chat.browse.open") : t("chat.browse.join")}
								aria-label={
									row.joined
										? t("chat.browse.openNamed", { name: channelName(row) })
										: t("chat.browse.joinNamed", { name: channelName(row) })
								}
								{...(row.joined ? {} : { disabled: join.busy })}
								onClick={() => (row.joined ? openChannel(row) : void join.run(row))}
							/>
						</li>
					))}
				</ul>
			)}
			{readerIsAdmin ? (
				<OtherChannels
					open={open}
					query={query}
					onView={(slug) => {
						onOpenChange(false)
						goTo(conversationPath(slug))
					}}
				/>
			) : null}
		</Dialog>
	)
}

/** Whether an administrator's row answers the dialog's field: its short name or its name. */
function matchesOther(row: AdminConversation, query: string): boolean {
	const q = query.trim().toLocaleLowerCase("en-GB")
	return (
		q === "" ||
		row.slug.toLocaleLowerCase("en-GB").includes(q) ||
		row.name.toLocaleLowerCase("en-GB").includes(q)
	)
}

/**
 * *Other channels*: the administrator's listing, read once per opening of the dialog. A row says the
 * channel's address, its kind with its groups and how many people are in it, whether it is archived,
 * and offers *View* — never *Join*, which is the outside view's decision, made with the channel's
 * members in sight.
 */
function OtherChannels({
	open,
	query,
	onView,
}: {
	open: boolean
	query: string
	onView: (slug: string) => void
}) {
	const groups = useModel((model) => model.groups)
	const [rows, setRows] = useState<AdminConversation[] | null>(null)
	const load = useAsyncAction(async () => {
		try {
			setRows((await listAdminConversations()).conversations)
		} catch (err) {
			setRows([])
			throw err
		}
	})
	const loadRows = load.run

	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (!open) return
		setRows(null)
		void loadRows()
	}, [open, loadRows])
	/* eslint-enable react-hooks/set-state-in-effect */

	const shown = rows?.filter((row) => matchesOther(row, query)) ?? null
	return (
		<section className="tb-form">
			<h3>{t("chat.browse.others.title")}</h3>
			<p className="tb-field__hint">{t("chat.browse.others.lead")}</p>
			<FormAlert messageKey={load.error} />
			{shown === null ? (
				<ListState.Loading rows={2} label={t("chat.browse.loading")} />
			) : shown.length === 0 ? (
				<p className="tb-empty">{t("chat.browse.others.empty")}</p>
			) : (
				<ul className="tb-browse__list">
					{shown.map((row) => {
						const name = `#${row.slug}`
						return (
							<li key={row.id} className="tb-browse__row">
								<span className="tb-browse__about">
									<span className="tb-browse__name">{name}</span>
									<span className="tb-browse__meta">
										{t("chat.browse.others.line", {
											kind: channelKindLabel(row.kind, row.ownerGroupIds, groups),
											members: t("chat.conversation.members", { count: row.memberCount }),
										})}
									</span>
								</span>
								{row.archived ? (
									<span className="tb-pill">{t("chat.browse.others.archived")}</span>
								) : null}
								<Button
									variant="neutral"
									size="sm"
									label={t("chat.browse.others.view")}
									aria-label={t("chat.browse.others.viewNamed", { name })}
									onClick={() => onView(row.slug)}
								/>
							</li>
						)
					})}
				</ul>
			)}
		</section>
	)
}
